import { NgZone, Inject, Component, OnInit, OnDestroy, Input, Optional, ChangeDetectorRef } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { Subscription, filter } from 'rxjs';

import * as am4core from '@amcharts/amcharts4/core';
import moment from 'moment-timezone';
import { CircleBullet, ColumnSeries, LabelBullet, LineSeries, LineSeriesDataItem, PieSeries } from '@amcharts/amcharts4/charts';
import { MapPolygon } from '@amcharts/amcharts4/maps';
import { cloneDeep } from 'lodash';

import { ReportBlockTypeEnum } from '../../../Reports/enums/report-block-type.enum';
import { ChartSegmentModel } from '../../../Reports/models/chart-segment.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { BarChartModel } from '../../../Charts/models/barChart/bar-chart.model';
import { MapChartModel } from '../../../Charts/models/map-chart.model';
import { DateAndValueXYChart } from '../../../Charts/models/date-and-value-x-y.chart';
import { DataPointModel } from '../../../Charts/models/data-point.model';
import { ReportSeriesModel } from '../../../Reports/models/report-series.model';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { HeatChartModel } from '../../../../Common/Charts/models/heatChart/heat-chart.model';
import { DateAndValueXYChartOptionsModel } from '../../../../Common/Charts/models/date-and-value-x-y-chart-options.model';
import { DateAndValueXYChartUnitEnum } from '../../../../Common/Charts/enums/date-and-value-x-y-chart-unit.enum';
import { defaultSeriesColorConfig } from '../../../../Common/Charts/config/default-series-color.config';
import { chartColors } from '../../../../Common/Charts/enums/chart-colors.enum';
import { chartMetricsToDateAndValueXYChartUnitMap } from '../../../../Common/Reports/enums/chart-metrics.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { DateValueXYChart } from '../../../Charts/models/date-value-x-y-chart.model';
import { ChartModel } from '../../../Charts/models/chart.model';
import { ReportsSeriesDataMergerService } from '../../../Reports/services/reports-series-data-merger.service';
import { CategoryValueXYChart } from '../../../Charts/models/category-value-x-y-chart.model';
import { ChartSpecificTypesEnum } from '../../../Reports/enums/chart-specific-types.enum';
import { ColorModel } from '../../../Forms/models/color.model';
import { PreviousPeriodBarColorsEnum } from '../../../Reports/enums/previous-period-bar-colors.enum';
import { ChartComponent } from '../../../Charts/components/Chart/chart.component';
import {
    MentionSearchCriteriaModel
} from '../../../../Monitor/Shared/models/mention-search-criteria.model';
import { safeDivide } from '../../../../Core/utils';
import { StackedRiverChartModel } from '../../../../Common/Charts/models/StackedRiverChart/stacked-river-chart.model';
import { SentimentModel } from '../../../../Monitor/Shared/models/sentiment.model';


@Component({
    selector: 'agpr-reports-chart',
    templateUrl: './reports-chart.component.html',
    styleUrls: ['./reports-chart.component.scss'],
    standalone: false
})
export class ReportsChartComponent extends ChartComponent implements OnInit, OnDestroy {

    @Input() public disableViewMentionLink: boolean = false;

    private preferences: PreferencesModel;
    private subscriptions: Subscription[] = [];

    readonly userBrowserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    constructor(
        public seriesDataMergerService: ReportsSeriesDataMergerService,
        public zone: NgZone,
        public changeDetectorRef: ChangeDetectorRef,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        @Optional() protected localizationService?: LocalizationService,
        @Optional() protected currentUserStateService?: CurrentUserStateService,
    ) {
        super(seriesDataMergerService, zone, changeDetectorRef);
    }

    ngOnInit() {
        if (this.currentUserStateService) {
            this.subscriptions.push(
                this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                    preferences => {
                        this.preferences = preferences;
                    }
                )
            );
        }
    }

    public initializeChart(): void {
        if (!this.viewInitted) {
            return;
        }

        super.initializeChart();

        // subscribe to a thing that tells us when it's ok to run the setup code
        this._chart?.ready.subscribe(value => {
            if (value) {
                this.setupSegmentAdapter();
            }
        });
    }

    public watchLoading(): void {
        super.watchLoading();
    }

    private getLinkLabel(isSocial: string = ''): string {
        let value;
        if (isSocial) {
            value = this.localizationService.transform('Social.intelligence.themeAnalytics.tooltip.actionLabel');
        } else {
            value = this.localizationService.transform('Reports.chart.segmentCoverageAnalysisLabel');
        }
        return value;
    }

    /**
     * The keys are the same for most charts, but there are a couple exceptions.
     * The bar chart uses different keys, and the line chart doesn't want a percent label at all, for example.
     *
     * @param percentKey the string identifying the property on the DataPoint that corresponds with percentage
     * @param countKey the string identifying the property on the DataPoint that corresponds with count
     */
    private getCountAndPercentLabel(
        segment: ChartSegmentModel,
        percentKey = 'value.percent',
        countKey = 'value',
        noPercentage = false
    ): string {
        let countAndPercentLabel = '';
        switch (this._chart.metadata.type as ReportBlockTypeEnum) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                return ` {${countKey}.formatNumber('#.a')}`;
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
            case ReportBlockTypeEnum.REFERRAL_TREND:
            case ReportBlockTypeEnum.PROMINENCE_OVER_TIME:
            case ReportBlockTypeEnum.RECENT_TOPICS:
                if (!segment.numberFormat) {
                    segment.numberFormat = (<DateAndValueXYChartOptionsModel>this._chart.options).unit;
                }
                if (segment.numberFormat ===  DateAndValueXYChartUnitEnum.CURRENCY) {
                    return  ` {${countKey}.formatNumber('#,###.00a')}`;
                } else if (segment.numberFormat === DateAndValueXYChartUnitEnum.NUMBER) {
                    return ` {${countKey}.formatNumber('#,###a')}`;
                } else if (segment.numberFormat === DateAndValueXYChartUnitEnum.PERCENTAGE) {
                    return  `{${countKey}.formatNumber(\'#.#\')}%`;
                }
        }

        if (this._chart.metadata.enableDataDisplaySettings) {
            if (this._chart.metadata.labelDisplayOptions) {
                if (noPercentage) {
                    countAndPercentLabel += `{${countKey}}`;
                } else {
                    countAndPercentLabel += `{${percentKey}.formatNumber(\'#.#\')}% ({${countKey}})`;
                }
            }
        } else if (countKey) {
            countAndPercentLabel += `{${countKey}}`;
        }
        return countAndPercentLabel;
    }


    /**
     * @param segment chart segment
     * @param percentKey key to get percent value
     * @param countKey key to get count value,
     * @param valueLabel label for the value (eg: series name, sentiment type)
     * @param tooltipIcon: tooltip icon
     * @param doLink determine if tooltip should have view mention link
     * @param hasPreviousPeriod used in a tooltip that's displayed for multiple periods at the same time
     * @param isPreviousPeriod used in a tooltip that's displayed for a single previous period segment
     * @returns the html for the tooltip when hover over the chart data
     */
    private getSegmentTooltipHTML(
        segment: ChartSegmentModel,
        percentKey?: string,
        countKey?: string,
        valueLabel?: string,
        tooltipIcon?: string,
        doLink = true,
        hasPreviousPeriod?: boolean,
        isPreviousPeriod?: boolean,
        previousPeriodCountKey: string = 'xValuePreviousPeriod',
        previousPeriodPercentKey: string = percentKey,
        previousPeriodIcon?: string,
        noPercentage?: boolean,
    ): string {
        if (this.disableViewMentionLink) {
            doLink = false;
        }
        if (!previousPeriodIcon && tooltipIcon) {
            previousPeriodIcon = tooltipIcon;
        }

        const previousPeriod =  this.localizationService ? this.localizationService.transform('Reports.series.previousPeriod') : 'Previous Period';
        const currentPeriod =  this.localizationService ? this.localizationService.transform('Reports.series.currentPeriod') : 'Current Period';
        const baseId = this.getSegmentId(segment);
        const html = `
            <div
                ${baseId ? `id="${baseId}"` : ''}
                style="margin: 10px; font-size: 0.875rem; width:100%;"
            >
                <div style="border-bottom: 1px solid #ced4da; padding-bottom: 5px">
                    <b style="font-weight: 600">
                        ${segment.label}${isPreviousPeriod ? ` (${previousPeriod})` : ''}
                    </b>
                </div>
                <div style="margin-top: 10px; display: flex; align-items: center; justify-content: flex-start;">
                    ${tooltipIcon ? this.getTooltipIcon(segment, tooltipIcon) : ''}
                    <p style="margin-right: 10px; word-break: break-word;">
                        ${valueLabel ? `<b style="font-weight: 600;"> ${valueLabel}:</b>` : ''}
                        ${hasPreviousPeriod ? `<b style="font-weight: 600;"> ${currentPeriod}:</b>` : ''}
                        ${this.getCountAndPercentLabel(segment, percentKey, countKey, noPercentage)}
                    </p>
                    ${doLink ? this.getTooltipLink(segment, isPreviousPeriod) : ''}
                </div>
                ${hasPreviousPeriod ? `
                    <div style="margin-top: 10px; display: flex; align-items: center">
                    ${previousPeriodIcon ? this.getTooltipIcon(segment, previousPeriodIcon, true) : ''}
                    <p style="margin-right: 10px; word-break: break-word;">
                        <b style="font-weight: 600;"> ${previousPeriod}:</b>
                        ${this.getCountAndPercentLabel(segment, previousPeriodPercentKey, previousPeriodCountKey)}
                    </p>
                    ${doLink ? this.getTooltipLink(segment, true) : ''}
                    </div>` : ''}
            </div>
        `;
        return html;
    }

    private getTooltipIcon(segment: ChartSegmentModel, icon: string, previousPeriod?: boolean) {
        return `<i
                    class="${icon}"
                    style="display: flex; color: ${previousPeriod ? segment.previousBarColor : segment.color}; margin-right: 10px;"
                ></i>`;
    }

    private getTooltipLink(segment: ChartSegmentModel, isPreviousPeriod?: boolean, isSocial: string = '') {
        // we want to disable the tooltip in preview
        if (this._chart.isPreview) {
            return `<div style="
                        margin-left: auto;
                        min-width: 100px;
                        align-self: flex-end;
                        ">
                        <span
                            style="color: #5E6473;
                            font-size: 0.875rem;
                            cursor: not-allowed;
                        "
                        >
                        ${this.getLinkLabel()}
                        </span>
                    </div>`;
        } else {
            const viewUrl = isPreviousPeriod ? segment.viewPreviousUrl : segment.viewUrl;
            if (viewUrl) {
                const fullLink: string = this.appBaseHref + viewUrl;
                const baseId = this.getSegmentId(segment);
                const linkId = baseId ? `id="${baseId}.viewMentionLink"` : '';
                return `<div>
                        <a
                            ${linkId}
                            class="${['viewMentionLink', isSocial].filter(Boolean).join(' ')}"
                            href="${fullLink}"
                            style="color: inherit;
                                text-decoration: underline;
                                font-size: 0.875rem;
                                ${fullLink ? '' : 'pointer-events: none; cursor: default;'}
                            "
                        >
                        ${this.getLinkLabel(isSocial)}
                    </a>
                </div>`;
            } else {
                return '';
            }
        }
    }

    private getMultiSeriesSharedTooltipHTML(
        segment: ChartSegmentModel,
        value: string,
        valueName?: string,
        icon?: string,
        doLink: boolean = true,
        isSocial: string = ''
    ): string {
        if (this.disableViewMentionLink) {
            doLink = false;
        }
        const html = `
            <div style="margin-top: 10px; display: flex; align-items: center">
                ${icon ? this.getTooltipIcon(segment, icon) : ''}
                <p style="margin-right: 20px">
                    ${valueName ? `<b style="font-weight: 600"> ${valueName}:</b>` : ''}
                    ${value}
                </p>
                ${doLink ? this.getTooltipLink(segment, undefined, isSocial) : ''}
            </div>
        `;
        return html;

    }

    public setupSegmentAdapter(): void {
        switch (this._chart.metadata.type) {
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN:
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
            case ReportBlockTypeEnum.SHARE_OF_VOICE:
            case ReportBlockTypeEnum.COVERAGE_COMPARISON:
            case ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK:
            case ReportBlockTypeEnum.TOP_REFERRAL_SOURCES:
                if (this._chart.options.chartSpecificType === ChartSpecificTypesEnum.PIE) {
                    this.setupPieChartSegmentAdapter();
                } else {
                    this.setupCategoryValueXYChartSegmentAdapter();
                }
                break;

            case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
                if (this._chart.options.chartSpecificType === ChartSpecificTypesEnum.MAP) {
                    this.setupMapChartSegmentAdapter();
                } else {
                    this.setupCategoryValueXYChartSegmentAdapter();
                }
                break;

            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
            case ReportBlockTypeEnum.TOP_HASHTAGS:
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
            case ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS:
                this.setupCategoryValueXYChartSegmentAdapter();
                break;

            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
            case ReportBlockTypeEnum.PROMINENCE_OVER_TIME:
            case ReportBlockTypeEnum.RECENT_TOPICS:
            case ReportBlockTypeEnum.REFERRAL_TREND:
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                if (this._chart instanceof DateValueXYChart) {
                    let doLink = true;
                    if (this._chart.metadata.type === ReportBlockTypeEnum.REFERRAL_TREND
                        || this._chart.metadata.type === ReportBlockTypeEnum.COVERAGE_IMPACT_TREND
                    ) {
                        doLink = false;
                    }
                    this.setUpDateValueXYChartSegmentAdapter(doLink);
                } else {
                    this.setupLineChartSegmentAdapter();
                }
                break;

            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON:
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON:
            case ReportBlockTypeEnum.SENTIMENT_BY_MEDIA_TYPE:
                this.setupCategoryValueXYChartSegmentAdapter();
                break;

            case ReportBlockTypeEnum.PROMINENCE_BY_REACH:
                this.setupHeatChartSegmentAdapter();
                break;
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                if (this._chart instanceof DateValueXYChart) {
                    this.setUpDateValueXYChartSegmentAdapter();
                } else {
                    this.setupStackedRiverChartSegmentAdapter();
                }
                break;
        }
    }

    setupStackedRiverChartSegmentAdapter(): void {
        const chart = this._chart as StackedRiverChartModel;
        const seriesIds = [];
        const seriesLabels = [];
        (chart.options as DateAndValueXYChartOptionsModel).seriesDefinitions.forEach(series => {
            seriesIds.push(series.key);
            seriesLabels.push(series.label);
        });
        chart.amchart?.series.values.forEach(series => {
            series.bullets.values.forEach(bullet => {
                bullet.adapter.add('tooltipHTML', (text, target) => {
                    if (chart.options.hideTooltip) {
                        return '';
                    }
                    let tooltip =
                        `<div
                        style="margin: 10px; font-size: 0.875rem;"
                        >
                        <div
                            style="border-bottom: 1px solid #ced4da; padding-bottom: 5px"
                        >
                            <b style="font-weight: 600">${target.dataItem.dataContext['label']}</b>
                        </div>
                    `;
                    const segment = target.tooltipDataItem.dataContext as ChartSegmentModel;
                    chart.amchart?.series.values.forEach((item, index) => {
                        // we need to reverse the index to get the reversed order
                        index = chart.amchart?.series.values.length - 1 - index;
                        segment.color = chart.styles?.colorMap[seriesIds[index]];
                        segment.viewUrl = target.dataItem.dataContext[seriesIds[index] + '_viewMentionUrl'];
                        const multiSeriesSharedTooltip = this.getMultiSeriesSharedTooltipHTML(
                            segment,
                            `{${seriesIds[index]}} ({${seriesIds[index]}Percent.formatNumber(\'#.0\')}%)`,
                            seriesLabels[index],
                            'fa-solid fa-square'
                        );
                        tooltip += multiSeriesSharedTooltip;
                    });
                    tooltip += '</div>';
                    return tooltip;
                });
            });
            // set tooltip style
            series.tooltip.getFillFromObject = false;
            series.tooltip.background.fill = am4core.color(chartColors.agprWhite);
            series.tooltip.background.stroke = am4core.color('#ced4da');
            series.tooltip.background.cornerRadius = 8;
            series.tooltip.background.fillOpacity = 1;
            // clear tooltip background shadow
            series.tooltip.background.filters.clear();
            series.tooltip.label.padding(0, 0, 0, 0);
            series.tooltip.label.fill = am4core.color(chartColors.agprBlack);
            series.tooltip.label.interactionsEnabled = true;
            series.tooltip.keepTargetHover = true;
        });
    }

    private setupMapChartSegmentAdapter(): void {
        (this._chart as MapChartModel).outputSeries.forEach(series => {
            series.mapPolygons.template.adapter.add('tooltipHTML', (text, target) => {
                if (this._chart.options.hideTooltip) {
                    return '';
                }
                (<MapPolygon>target).tooltip.label.interactionsEnabled = true;
                (<MapPolygon>target).tooltip.keepTargetHover = true;
                const segment = (<MapPolygon>target).tooltipDataItem.dataContext as ChartSegmentModel;

                if (segment) {
                    return this.getSegmentTooltipHTML(segment, 'percent', 'value');

                } else {
                    return text;
                }
            });
            series.mapPolygons.template.hoverOnFocus = true;
            series.mapPolygons.template.events.on('hit', function(event) {
                const segment = event.target.tooltipDataItem.dataContext as ChartSegmentModel;
                (document.querySelector(`[class="viewMentionLink"][href*='${segment.viewUrl}']`) as HTMLElement)?.click();
            });
        });
    }

    private setUpDateValueXYChartSegmentAdapter(addLink: boolean = true): void {
        const chart = this._chart as DateValueXYChart;
        const isStackedChart = (chart.options as DateAndValueXYChartOptionsModel).isStacked;
        const stackedSeriesIds = [];
        const stackedSeriesLabels = [];
        if (isStackedChart) {
            chart.chartSeries.forEach(series => {
                stackedSeriesIds.push(series.key);
                stackedSeriesLabels.push(series.label);
            });
        }
        chart.amchart?.series.values.forEach(series => {
            let doLink = addLink;
            // the mention series in coverage impact trend should do link
            if (!doLink && series.dataFields['valueY'] === 'value0'
                && this._chart.metadata.type === ReportBlockTypeEnum.COVERAGE_IMPACT_TREND
            ) {
                doLink = true;
            }
            if (series instanceof LineSeries) {
                series.bullets.values.forEach(bullet => {
                    if (isStackedChart) {
                        this.setupStackedChartSegmentAdapter(chart, bullet, stackedSeriesIds, stackedSeriesLabels);
                    } else {
                        this.setUpSingleDataPointSegmentAdapter(chart, bullet, 'value', doLink);
                    }
                });
            }
            if (series instanceof ColumnSeries) {
                this.setUpSingleDataPointSegmentAdapter(chart, series.columns.template, 'value', doLink);
            }
        });
        this.setUpLineChartKeyboardInteraction(isStackedChart);
    }

    private setupCategoryValueXYChartSegmentAdapter(): void {
        const chart = this._chart as BarChartModel | CategoryValueXYChart;

        if (!chart.amchart?.series) {
            return;
        }

        if (chart instanceof BarChartModel) {
            chart.amseries.columns.template.tooltipX = 10;
            chart.amseries.tooltip.label.interactionsEnabled = true;
            chart.amseries.tooltip.keepTargetHover = true;
            chart.amseries.tooltip.pointerOrientation = 'left';
            this.addBarChartTooltipHTML(chart, (chart as BarChartModel).amseries.columns.template, 'xValue');
            this.addBarChartTooltipHTML(chart, (chart as BarChartModel).labelTemplate, 'xValue');
        } else {
            if (
                (chart.options.chartSpecificType === ChartSpecificTypesEnum.BAR ||
                    chart.options.chartSpecificType === ChartSpecificTypesEnum.MULTI_STACKED_BAR)
                && chart.amchart.series?.length === 1 && !chart.styles.chartDisplaySettings.previousPeriod
            ) {
                this.addBarChartTooltipHTML(chart, (chart as CategoryValueXYChart).categoryLabel, 'value0', 0);
            }
            chart.amchart.series.each((series: ColumnSeries, index: number) => {
                const chartSeriesIndex = +series.dataFields['percent']?.match(/\d+/)[0];
                if (chart.metadata.type === ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN && chart.amchart.series.length === 1) {
                    this.addBarChartTooltipHTML(chart, (series as ColumnSeries).columns?.template, 'value0');
                } else {
                    this.addBarChartTooltipHTML(chart, (series as ColumnSeries).columns?.template,
                        'value' + chartSeriesIndex, chartSeriesIndex, chart.chartSeries[chartSeriesIndex]?.label
                    );
                }
            });
            this.setUpColumnSeriesPreviousPeriodStyles(chart);
            if (chart.options.imageAsCategory) {
                chart.categoryAxis.dataItems.template.bullet?.adapter.add('tooltipHTML', (text, target) => {
                    return chart.options.hideTooltip ? '' : this.getColumnSeriesPreviousPeriodTooltipText(text, target, chart);
                });
            }
        }
    }

    private addBarChartTooltipHTML(
        chart: BarChartModel | CategoryValueXYChart,
        template: any,
        countKey: string,
        seriesIndex?: number,
        valueLabel: string = ''
    ): void {
        if (!template) {
            return;
        }
        template.adapter.add('tooltipHTML', (text, target) => {
            const segment = target.tooltipDataItem?.dataContext as ChartSegmentModel;
            if (!segment || chart.options.hideTooltip) {
                return '';
            }
            segment.color = chart.styles.colorMap?.['barColor']  || chart.styles.colorMap?.['seriesColor0'] || chartColors.agprBlue;

            if (segment) {
                const clonedSegment = cloneDeep(segment);
                clonedSegment.viewUrl = clonedSegment['viewUrl' + seriesIndex] || clonedSegment.viewUrl
                    || clonedSegment['value' + seriesIndex + '_viewUrl'];
                clonedSegment.color = this.chart.styles.colorMap[chart.chartSeries[seriesIndex]?.key]
                    || this.chart.styles.colorMap[segment.key] || clonedSegment.color;
                const date = clonedSegment.mappingKeys?.['date'];
                // date is part of mapping
                if (date < 2) {
                    if (valueLabel) {
                        if (date === 0 && clonedSegment['value1'] >= 0) {
                            clonedSegment.label = this.localizationService ?
                                this.localizationService.transform('Reports.series.previousPeriod') : 'Previous Period';
                        } else {
                            clonedSegment.label = this.localizationService ?
                                this.localizationService.transform('Reports.series.currentPeriod') : 'Current Period';
                        }
                    } else if (clonedSegment.mappingKeys['source']) {
                        clonedSegment.label = clonedSegment.mappingKeys['source'];
                    }
                    segment.label = clonedSegment.label;
                }

                if (chart.options.dataPointDistinctColor) {
                    let color = am4core.color(chart.styles.colorMap[target.dataItem.dataContext['key']]);
                    if (chart.options.useReportSeriesColor) {
                        let columnIndex = chart.amchart.data.findIndex(x => x.key === target.dataItem.dataContext['key']);
                        if (columnIndex > -1) {
                            if (chart.options.reverseBarSeriesOrder) {
                                columnIndex = chart.amchart.data.length - 1 - columnIndex;
                            }
                            color = am4core.color(chart.styles.colorMap['seriesColor' + columnIndex]);
                        }
                    }
                    clonedSegment.color = color;
                } else {
                    let color = am4core.color(chart.styles.colorMap['barColor'] || chart.styles.colorMap['seriesColor0'] || chartColors.agprBlue);
                    const chartSeries = chart.chartSeries[seriesIndex];
                    if (chartSeries?.key) {
                        color = am4core.color(chart.styles.colorMap[chartSeries.key]);
                    }
                    if (chartSeries?.colorMapKey) {
                        color = am4core.color(chart.styles.colorMap[chartSeries.colorMapKey]);
                    }
                    clonedSegment.color = color;
                }

                return this.getSegmentTooltipHTML(clonedSegment,
                    'percent',
                    countKey,
                    valueLabel,
                    'fa-solid fa-square',
                    !!clonedSegment.viewUrl,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    chart.options.noPercentage
                );

            } else {
                return text;
            }
        });
        template.hoverOnFocus = true;
        const clickEvent = template.events.on('hit', function(event) {
            const segment = event.target.tooltipDataItem.dataContext as ChartSegmentModel;
            const link = segment.viewUrl ? segment.viewUrl : segment.viewPreviousUrl;
            (document.querySelector(`[class="viewMentionLink"][href*='${link}']`) as HTMLElement)?.click();
        });
        if (chart.options.noActionOnColumnClick) {
            clickEvent.dispose();
        }
    }

    private setUpColumnSeriesPreviousPeriodStyles(chart: CategoryValueXYChart): void {
        switch (chart.options.chartSpecificType) {
            case ChartSpecificTypesEnum.BAR:
            case ChartSpecificTypesEnum.COLUMN:
                chart.amchart.series.each((series: ColumnSeries, index: number) => {
                    const columnTemplate = series.columns.template;
                    const lengthIdentifier = chart.options.chartSpecificType === ChartSpecificTypesEnum.BAR ? 'height' : 'width';
                    const hasPreviousPeriod = chart.styles.chartDisplaySettings?.previousPeriod && chart.amchart.series.length > 1;
                    if (!hasPreviousPeriod) {
                        columnTemplate.adapter.remove('fillOpacity');
                        columnTemplate.adapter.remove('strokeDasharray');
                    } else {
                        columnTemplate[lengthIdentifier] = am4core.percent(index === 0 && hasPreviousPeriod ? 100 : 50);
                        columnTemplate.adapter.add('fill', (event, target) => {
                            // since in clustered bar we reverse bar order, in this case previous period index is 1
                            if (index === 0 && hasPreviousPeriod && chart.styles.previousPeriodBarColorType === PreviousPeriodBarColorsEnum.CUSTOM) {
                                return am4core.color(chart.styles.colorMap.previousPeriodBarColor);
                            } else {
                                return event;
                            }
                        });
                        columnTemplate.adapter.add('stroke', (event, target) => {
                            if (index === 0 && hasPreviousPeriod && chart.styles.previousPeriodBarColorType === PreviousPeriodBarColorsEnum.CUSTOM) {
                                return am4core.color(chart.styles.colorMap.previousPeriodBarColor);
                            } else {
                                return event;
                            }
                        });
                        columnTemplate.adapter.add('fillOpacity', (event, target) => {
                            return index === 0 && hasPreviousPeriod ? 0.4 : 1;
                        });
                        columnTemplate.adapter.add('strokeDasharray', (event, target) => {
                            return index === 0 && hasPreviousPeriod ? '3,2' : event;
                        });
                        (series.bullets.getIndex(0) as LabelBullet)?.label.adapter.add('text', (event, target) => {
                            return index === 0 && hasPreviousPeriod ? '' : event;
                        });
                        series.clustered = false;

                        // make the tooltip appear starting 10px from the left of the column
                        series.columns.template.tooltipX = 10;
                        series.tooltip.label.interactionsEnabled = true;
                        series.tooltip.keepTargetHover = true;
                        series.tooltip.pointerOrientation = 'horizontal';
                        series.columns.template.adapter.add('tooltipHTML', (text, target) => {
                            return chart.options.hideTooltip ? '' : this.getColumnSeriesPreviousPeriodTooltipText(text, target, chart);
                        });
                    }
                });
                break;
            case ChartSpecificTypesEnum.MULTI_STACKED_BAR:
            case ChartSpecificTypesEnum.MULTI_STACKED_COLUMN:
                chart.amchart.series.each((series: ColumnSeries, index: number) => {
                    const columnTemplate = series.columns.template;
                    if (!chart.styles.chartHeaderSettings?.previousPeriod) {
                        columnTemplate.adapter.remove('fillOpacity');
                        columnTemplate.adapter.remove('strokeOpacity');
                    } else {
                        columnTemplate.adapter.add('fillOpacity', (event, target) => {
                            const dataContext = target.dataItem.dataContext;
                            return dataContext['mappingKeys']?.['date'] === 0 ? 0.3 : 1;
                        });
                        columnTemplate.adapter.add('strokeOpacity', (event, target) => {
                            const dataContext = target.dataItem.dataContext;
                            return dataContext['mappingKeys']?.['date'] === 0 ? 0.1 : 1;
                        });
                        (series.bullets.getIndex(0) as LabelBullet).label.adapter.add('fill', (event, target) => {
                            const dataContext = target.dataItem.dataContext;
                            return dataContext['mappingKeys']?.['date'] === 0 ? am4core.color('#000') : event;
                        });
                    }
                });
                break;
        }
    }

    getColumnSeriesPreviousPeriodTooltipText(text: any, target: any, chart: CategoryValueXYChart): string {
        const segment = target.tooltipDataItem.dataContext as ChartSegmentModel;
        let color;
        if (chart.options.dataPointDistinctColor) {
            color = chart.styles.colorMap[target.dataItem.dataContext['key']];
            if (chart.options.useReportSeriesColor) {
                let columnIndex = chart.amchart.data.findIndex(x => x.key === target.dataItem.dataContext['key']);
                if (columnIndex > -1) {
                    if (chart.options.reverseBarSeriesOrder || chart.options.reverseSourceOrder) {
                        columnIndex = chart.amchart.data.length - 1 - columnIndex;
                    }
                    color = chart.styles.colorMap['seriesColor' + columnIndex];
                }
            }
            color = new ColorModel(color, null, null);
        }
        segment.color = color.hex;
        if (chart.styles.previousPeriodBarColorType === PreviousPeriodBarColorsEnum.USE_BAR) {
            segment['previousBarColor'] = '#' + ColorModel.RGBtoHEX(color.applyOpacity(0.3).rgb);
        } else {
            segment['previousBarColor'] = chart.styles.colorMap['previousPeriodBarColor'];
        }
        const previousPeriodCountKey = 'previousPeriodCountKey';
        const previousPeriodPercentKey = 'previousPeriodPercentKey';

        if (chart.styles.chartHeaderSettings?.previousPeriod) {
            segment['viewUrl'] = segment['viewUrl1'] as string;
            segment[previousPeriodCountKey] = segment['value0'];
            segment[previousPeriodPercentKey] = segment['percent0'];
            segment['viewPreviousUrl'] = segment['viewUrl0'];
            if (segment['valueLabel']) {
                segment.label = segment['valueLabel'] as string;
            }
        } else {
            segment['viewUrl'] = segment['viewUrl0'] as string;
            segment['value1'] = segment['value0'];
            segment['percent1'] = segment['percent0'];
        }

        if (segment) {
            return this.getSegmentTooltipHTML(
                segment, 'percent1', 'value1', '', 'fa-solid fa-square', !!segment.viewUrl || !!segment['viewPreviousUrl'],
                chart.styles.chartHeaderSettings?.previousPeriod, false, previousPeriodCountKey, previousPeriodPercentKey
            );
        } else {
            return text;
        }
    }


    private setupLineChartSegmentAdapter(): void {
        const chart = this._chart as DateAndValueXYChart;
        chart.bullets?.forEach(bullet => this.setUpSingleDataPointSegmentAdapter(chart, bullet, 'yValue'));
        this.setUpLineChartKeyboardInteraction();
    }

    private setUpSingleDataPointSegmentAdapter(chart: ChartModel, element: any, countKeyBase: string, doLink: boolean = true): void {
        element.adapter.add('tooltipHTML', (text, target) => {
            if (chart.options.hideTooltip) {
                return '';
            }
            const tooltip = target.tooltip;
            tooltip.label.interactionsEnabled = true;
            tooltip.keepTargetHover = true;

            if ((chart.amchart?.maxRightSelf > 0) && (chart.amchart?.maxBottomSelf > 0)) {
                tooltip.setBounds({
                    x: 0,
                    y: 0,
                    width: chart.amchart.maxRightSelf,
                    height: chart.amchart.maxBottomSelf
                });
            } else {
                // just set a default height/width.. else the tooltip goes to the top of the chart always and the user can't click
                tooltip.setBounds({
                    x: 0,
                    y: 0,
                    width: 500,
                    height: 400
                });
            }

            const segment = target.tooltipDataItem.dataContext as ChartSegmentModel;

            if (segment) {
                // @HACK - because of the way data is flattened in the multi-series case,
                // the data associated with a single bullet (segment) is for multiple series.
                // in order to figure out what series we actually want, the bullet has a dummy field
                // 'propertyFields.id', which is the index of the series.
                // Additionally, when adding the series data to the segment, the series is suffixed with
                // the index.
                // ex: dataPoint['series${SOME INDEX}Type']
                // and dataPoint['series${SOME INDEX}Id']
                // instead of just dataPoint['seriesType']
                // this solution is probably not ideal - @TODO look into re-writing the multiseries line chart
                const _segment = { // naive shallow clone
                    ...segment
                };

                let countKey = countKeyBase;
                const dataItem = target.dataItem as LineSeriesDataItem;
                if (!chart.singleSeries && target.propertyFields.id) {
                    _segment.viewUrl = <string>_segment[`series${target.propertyFields.id}Url`]
                        || <string>_segment['viewUrl' + target.propertyFields.id];
                    countKey += target.propertyFields.id;
                }

                // for prominence over time chart, yValue is average prominence instead of mentions
                // thus we display mentionCount instead of yValue
                if (this._chart.metadata.type === ReportBlockTypeEnum.PROMINENCE_OVER_TIME) {
                    countKey = 'mentionCount';
                }


                const dataContext = dataItem.dataContext as DataPointModel;
                let first = dataContext;
                let last = dataContext;

                _segment.label = dataContext.label;
                _segment.yValue = dataContext.yValue;

                if (dataItem.groupDataItems?.length > 1) {
                    const items = dataItem.groupDataItems;
                    first = items[0].dataContext as DataPointModel;
                    last = items[items.length - 1].dataContext as DataPointModel;
                    const startDate: string = this.preferences ?
                        moment(first.rawStartDate || first.rawDateFacetFrom).tz(this.preferences.timeZone).format('ddd MMM D YYYY') :
                        moment(first.rawStartDate || first.rawDateFacetFrom).tz(this.userBrowserTimezone).format('ddd MMM D YYYY');
                    const endDate: string = this.preferences ?
                        moment(last.rawEndDate || last.rawDateFacetTo).tz(this.preferences.timeZone).format('ddd MMM D YYYY') :
                        moment(last.rawEndDate || last.rawDateFacetTo).tz(this.userBrowserTimezone).format('ddd MMM D YYYY');

                    _segment.label = `${startDate} - ${endDate}`;
                    _segment.yValue = items.reduce((value, item) => value + item.valueY, 0);
                }

                // target.propertyFields.id should be the index of the series, or unset if it's a single series chart
                let seriesIndex: number = +target.propertyFields.id ? +target.propertyFields.id : 0;
                const chartSeriesIndex: number = +target.propertyFields.id ? +target.propertyFields.id : 0;
                const reportSeries = chart.rawSeries as ReportSeriesModel[];
                // Some single series chart allows multi analysis metrics, each metric has its own line/axis so the amchart treats them as multi series,
                // so 'target.propertyFields.id' in this case won't be the correct series index.
                // Instead, we would want to check if there're multi metrics in the series, then always set the series index to 0
                if (reportSeries[0].metrics?.length > 0) {
                    seriesIndex = 0;
                    if (!(chart instanceof DateValueXYChart)) {
                        countKey += `_${target.propertyFields['metric']}`;
                    }
                }

                const series = reportSeries[seriesIndex] || reportSeries[0];
                const icon = ((chart.options.chartSpecificType === ChartSpecificTypesEnum.LINE
                    || chart.options.chartSpecificType === ChartSpecificTypesEnum.MULTI_LINE))
                    ? 'fa-solid fa-code-commit' : 'fa-solid fa-square';
                // retroactively fix the 'viewUrl' for this segment -
                // it represents multiple data points
                // todo: check whether this filterOverrides can exist only for grouping data
                const dateRange = new DateQueryModel();
                dateRange.type = DateQueryTypeEnum.ABSOLUTE;
                dateRange.absoluteRange = {
                    start: new Date((first.rawStartDate || first.rawDateFacetFrom) as number),
                    end: new Date((last.rawEndDate || last.rawDateFacetTo) as number)
                };
                dateRange.dateType = series.dateRange.dateType;

                const filters = new MentionSearchCriteriaModel();
                filters.showSimilar = series.mentions.filters?.searchCriteria?.showSimilar || true;
                filters.query.dateQuery = DateQueryModel.makeAbsoluteRange(dateRange, this.preferences?.timeZone);

                // accessible label for bullet
                target.readerTitle = `${_segment['valueLabel' + chartSeriesIndex]}, ${_segment['label']}, {${countKey}}`;

                _segment.viewUrl = series.getMentionSeriesUrl(filters);
                _segment.color = chart.styles?.colorMap[chart.chartSeries[chartSeriesIndex]?.colorMapKey]
                    || chart.styles?.colorMap[`metric${chartSeriesIndex}`]
                    || chart.styles?.colorMap[`seriesColor${chartSeriesIndex}`]
                    || defaultSeriesColorConfig[chartSeriesIndex % defaultSeriesColorConfig.length];
                _segment.numberFormat = chartMetricsToDateAndValueXYChartUnitMap.get(target.propertyFields['metric']);
                _segment['_metricIndex'] = chartSeriesIndex;

                return this.getSegmentTooltipHTML(
                    _segment,
                    null,
                    countKey,
                    _segment['valueLabel' + chartSeriesIndex] as string,
                    icon,
                    doLink
                );

            } else {
                return text;
            }
        });
    }

    setupStackedChartSegmentAdapter(chart, element, seriesIds, seriesLabels): void {
        const isSocial = chart.metadata.isSocial;
        element.adapter.add('tooltipHTML', (text, target) => {
            if (chart.options.hideTooltip) {
                return '';
            }
            let tooltipLabel = target.dataItem.dataContext['label'];
            const timeScale = target.dataItem.dataContext['timeScale'];
            if (timeScale === 'hour') {
                tooltipLabel = moment(target.dataItem.dataContext['key']).format('MMM DD, YYYY HH:mm');
            }
            let tooltip =
                `<div
                        style="margin: 10px; font-size: 0.875rem;"
                    >
                        <div
                            style="border-bottom: 1px solid #ced4da; padding-bottom: 5px"
                        >
                            <b style="font-weight: 600">${tooltipLabel}</b>
                        </div>
                    `;
            const segment = target.tooltipDataItem.dataContext as ChartSegmentModel;
            const seriesIndex: number = +target.propertyFields.id ? +target.propertyFields.id : 0;
            const series = isSocial ? (chart.rawSeries as ReportSeriesModel[])[0] : (chart.rawSeries as ReportSeriesModel[])[seriesIndex];
            chart.amchart?.series.values.forEach((item, index) => {
                // we need to reverse the index to get the reversed order
                index = chart.amchart?.series.values.length - 1 - index;
                const dataItem = target.dataItem as LineSeriesDataItem;
                const dataContext = dataItem.dataContext as DataPointModel;
                let first = dataContext;
                let last = dataContext;

                segment.label = dataContext.label;

                if (dataItem.groupDataItems?.length > 1) {
                    const items = dataItem.groupDataItems;
                    first = items[0].dataContext as DataPointModel;
                    last = items[items.length - 1].dataContext as DataPointModel;
                    const startDate: string = moment(first.rawStartDate || first.rawDateFacetFrom)
                        .tz(this.preferences?.timeZone || this.userBrowserTimezone).format('ddd MMM D YYYY');
                    const endDate: string = moment(last.rawEndDate || last.rawDateFacetTo)
                        .tz(this.preferences?.timeZone || this.userBrowserTimezone).format('ddd MMM D YYYY');

                    segment.label = `${startDate} - ${endDate}`;

                    segment['value' + index] = items.reduce((val, el) => val + el.dataContext['value' + index], 0);
                    if (!chart.isPreview) {
                        segment['percent' + index] = parseFloat((safeDivide(+segment['value' + index], segment.mentionCount) * 100).toFixed(1));
                    }
                }

                segment.color = chart.styles?.colorMap[seriesIds[index]] || defaultSeriesColorConfig[index % defaultSeriesColorConfig.length];
                const percent = segment['percent' + index];
                const value = segment['value' + index];

                const dateRange = new DateQueryModel();
                dateRange.type = DateQueryTypeEnum.ABSOLUTE;
                dateRange.absoluteRange = {
                    start: new Date((first.rawStartDate || first.rawDateFacetFrom) as number),
                    end: new Date((last.rawEndDate || last.rawDateFacetTo) as number)
                };
                if (!isSocial) {
                    dateRange.dateType = series.dateRange.dateType;
                }

                const filters = new MentionSearchCriteriaModel();
                filters.showSimilar = series.mentions.filters?.searchCriteria?.showSimilar || true;
                filters.query.dateQuery = DateQueryModel.makeAbsoluteRange(dateRange, this.preferences?.timeZone);
                filters.query.sentiments =[new SentimentModel(seriesIds[index])];
                if (isSocial) {
                    segment.viewUrl = series.getSocialSeriesUrl(filters);
                } else {
                    segment.viewUrl = series.getMentionSeriesUrl(filters);
                }
                const formatted = `${value} (${Number(percent).toFixed(1)}%)`;
                segment.mappingKeys = {...segment.mappingKeys, sentiment: seriesIds[index]};
                const multiSeriesSharedTooltip = this.getMultiSeriesSharedTooltipHTML(
                    segment,
                    formatted,
                    seriesLabels[index],
                    'fa-solid fa-square',
                    undefined,
                    isSocial
                );
                tooltip += multiSeriesSharedTooltip;
            });
            tooltip += '</div>';
            return tooltip;
        });
    }

    /**
     * Modify bullet DOM so that each bullet can be navigated by arrow left and right
     * Using TAB key will move focus between lines
     * Hit Enter on bullet point will open its mention link
     */
    setUpLineChartKeyboardInteraction(isStacked: boolean = false): void {
        const chart = this._chart as DateAndValueXYChart;
        chart.amchart?.events.on('dataitemsvalidated', function (event) {
            this.handleLineChartKeyboardInteraction(chart, isStacked);
        }, this);
        chart.amchart?.events.on('ready', function (event) {
            this.handleLineChartKeyboardInteraction(chart, isStacked);
        }, this);
    }

    private handleLineChartKeyboardInteraction(chart: DateAndValueXYChart, isStacked: boolean = false): void {
        const chartBullets = [].concat(...chart.bullets.map(x => x.clones.values.slice(chart?.amchart?.legend ? 1 : 0)));;

        for (let i = 0; i < chartBullets.length; i++) {
            const circleBullet = chartBullets[i];
            if (isStacked) {
                circleBullet.hoverOnFocus = true;
            }
            circleBullet.dom.addEventListener('keydown', (e) => {
                switch (e.key) {
                    case ActionKeys.RIGHT:
                        if (chartBullets[i + 1]?.propertyFields.id === circleBullet.propertyFields.id) {
                            e.preventDefault();
                            chartBullets[i + 1].dom.focus();
                        }
                        break;
                    case ActionKeys.LEFT:
                        if (chartBullets[i - 1]?.propertyFields.id === circleBullet.propertyFields.id) {
                            e.preventDefault();
                            chartBullets[i - 1].dom.focus();
                        }
                        break;
                    case ActionKeys.ENTER:
                        const viewMentionLink = circleBullet.tooltipHTML.match(/href="(.*)"/)[1];
                        if (viewMentionLink) {
                            // use extracted href from tooltip html instead of segment data so we don't have to deal with viewUrl
                            // for different type of line charts (eg: single series, multi series, multi metrics).
                            // See setupLineChartSegmentAdapter() for more info
                            (document.querySelector(`[class="viewMentionLink"][href*='${viewMentionLink}']`) as HTMLElement)?.click();
                        }
                        break;
                }
            });
        }
    }

    private setupPieChartSegmentAdapter(): void {
        this.chart.amchart?.series.values.forEach((series: PieSeries, index: number) => {
            series.slices.template.adapter.add('tooltipHTML', (text, target) => {
                if (
                    (target.tooltipDataItem?.dataContext?.['label'] === 'hasNoData'
                    && target.tooltipDataItem?.dataContext?.['value' + index] > 0)
                    || this.chart.options.hideTooltip
                ) {
                    return '';
                } else {
                    const segment = target.tooltipDataItem.dataContext as ChartSegmentModel;
                    if (segment) {
                        if (this.chart.amchart.series.length > 1) {
                            if (index === 0) {
                                segment.viewPreviousUrl = segment['viewUrl0'];
                            } else {
                                segment.viewUrl = segment['viewUrl1'] as string;
                            }
                        } else {
                            segment.viewUrl = segment['viewUrl0'] as string;
                        }
                        return this.getSegmentTooltipHTML(
                            segment,
                            'value.percent',
                            'value.value',
                            null,
                            'fa-solid fa-square',
                            true,
                            false,
                            this.chart.amchart.series.length > 1 && index === 0
                        );
                    }
                }
            });
        });

        // keyboard accessibility for tooltip
        this.chart.amchart?.series.values.forEach((series: PieSeries, index: number) => {
            series.slices.template.hoverOnFocus = true;
            //  @HACK - for some reason, creating 'hit' event listener on slice template doesn't work, so we need to add it in the dom
            series.slices.template.events.on('ready', function (event) {
                ((event.target as any).dom as any).addEventListener('keydown', (e) => {
                    if ((e as KeyboardEvent).key === 'Enter') {
                        const segment = event.target.tooltipDataItem.dataContext as ChartSegmentModel;
                        (document.querySelector(`[class="viewMentionLink"][href*='${segment['viewUrl' + index]}']`) as HTMLElement)?.click();
                    }
                });
            });
        });
    }

    private setupHeatChartSegmentAdapter(): void {
        const chart = this._chart as HeatChartModel;

        const labelFunc = (text, target) => {
            if (chart.options.hideTooltip) {
                return '';
            }

            const segment = target.tooltipDataItem.dataContext as ChartSegmentModel;
            if (segment) {
                let description: string;
                // Quadrant descriptions bubble should only appear when hovering over the quadrant title or the rectangle container
                if (segment.xValue === 0 && segment.yValue === 0) {
                    description = this.localizationService ?
                        this.localizationService.transform('Reports.prominenceByReach.niche.tooltip') : 'Passing mentions in smaller publications';
                } else if (segment.xValue === 1 && segment.yValue === 0) {
                    description = this.localizationService ?
                        this.localizationService.transform('Reports.prominenceByReach.prevalent.tooltip') : 'Passing mentions in larger publications';
                } else if (segment.xValue === 1 && segment.yValue === 1) {
                    description = this.localizationService ?
                        this.localizationService.transform('Reports.prominenceByReach.leader.tooltip') : 'Brand-focused mentions in larger publications';
                } else if (segment.xValue === 0 && segment.yValue === 1) {
                    description = this.localizationService ?
                        this.localizationService.transform('Reports.prominenceByReach.notable.tooltip') : 'Brand-focused mentions in smaller publications';
                }
                const tooltipHtml = `
                    <div
                        style="margin: 5px 0 10px 0; font-size: 0.875rem;"
                    >
                        <div>
                            <b style="font-weight: 600">
                                ${segment.label}
                            </b>
                        </div>
                        <div style="margin-top: 10px;">
                            <p style="word-break: break-word; line-height: 1.5;">
                                ${description}
                            </p>
                        </div>
                        <div style="display: flex; justify-content: space-between; align-items: center; line-height: 1.5; margin-top: 10px;">
                            <p>
                                ${this.getCountAndPercentLabel(segment, 'percent', 'count')}
                            </p>
                            ${this.getTooltipLink(segment, false)}
                        </div>
                    </div>`;
                return tooltipHtml;
            } else {
                return text;
            }
        };

        const valueFunc = (text, target) => {
            if (chart.options.hideTooltip) {
                return '';
            }

            const segment = target.tooltipDataItem.dataContext as ChartSegmentModel;
            if (segment) {
                const tooltipHtml = `
                    <div
                        style="margin: 5px 0 10px 0; font-size: 0.875rem;"
                    >
                        <div>
                            <b style="font-weight: 600">
                                ${segment.label}
                            </b>
                        </div>
                        <div style="margin-top: 10px; display: flex; justify-content: space-between; align-items: center; line-height: 1.5;">
                            <p style="margin-right: 20px;">
                                ${this.getCountAndPercentLabel(segment, 'percent', 'count')}
                            </p>
                            ${this.getTooltipLink(segment, false)}
                        </div>
                    </div>`;
                return tooltipHtml;
            } else {
                return text;
            }
        };

        [chart.countBullet, chart.labelBullet, chart.percentBullet, chart.circleBullet].forEach(bullet => {
            if (bullet) {
                bullet.tooltip.label.interactionsEnabled = true;
                bullet.tooltip.keepTargetHover = true;
            }
        });

        if (chart.countBullet) {
            chart.countBullet.adapter.add('tooltipHTML', valueFunc);
        }
        if (chart.percentBullet) {
            chart.percentBullet.adapter.add('tooltipHTML', valueFunc);
        }

        chart.labelBullet.adapter.add('tooltipHTML', labelFunc);
        chart.circleBullet.adapter.add('tooltipHTML', labelFunc);

        chart.amchart?.series.values.forEach(series => {
            const columnTemplate = (series as ColumnSeries).columns.template;
            columnTemplate.adapter.add('tooltipHTML', labelFunc);
            columnTemplate.tooltip.label.interactionsEnabled = true;
            columnTemplate.tooltip.keepTargetHover = true;
            columnTemplate.hoverOnFocus = true;
            columnTemplate.events.on('hit', function (event) {
                const segment = event.target.tooltipDataItem.dataContext as ChartSegmentModel;
                (document.querySelector(`[class="viewMentionLink"][href*='${segment.viewUrl}']`) as HTMLElement)?.click();
            });
        });
    }

    private getSegmentId(segment: ChartSegmentModel): string {
        const blockId = this._chart.metadata.id;
        const chartType = this._chart.metadata.type as ReportBlockTypeEnum;
        switch (chartType) {
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN:
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
            case ReportBlockTypeEnum.TOP_HASHTAGS:
            case ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS:
            case ReportBlockTypeEnum.TOP_REFERRAL_SOURCES:
            case ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK:
            case ReportBlockTypeEnum.PROMINENCE_BY_REACH:
                const index = this._chart.amchart?.data?.findIndex(d => d === segment || d.key === segment.key);
                if (index === -1 || index === undefined) {
                    return null;
                }
                return `${blockId}.tooltip.${index}`;
            case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
                const locationKey = segment.mappingKeys?.['location'];
                if (!locationKey) {
                    return null;
                }
                return `${blockId}.${String(locationKey).toLowerCase()}`;
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON: {
                const metricIndex: number = +(segment['_metricIndex'] ?? 0);
                const metricLabel = String(segment['valueLabel' + metricIndex] ?? segment.valueLabel ?? '')
                    .toLowerCase()
                    .replace(/\s+/g, '_')
                    .replace(/[()]/g, '');

                const dateIndex = segment.mappingKeys?.['dateIndex'];
                if (!metricLabel || dateIndex === undefined) {
                    return null;
                }
                return `${blockId}.${metricLabel}.${dateIndex}`;
            }
            case ReportBlockTypeEnum.SENTIMENT_TREND: {
                const sentiment = segment.mappingKeys?.['sentiment'];
                const date = segment.mappingKeys?.['date'];
                if (!sentiment || date === undefined) {
                    return null;
                }
                const dateIndex = this._chart.amchart?.data?.findIndex(d => d['key'] === date);
                if (dateIndex === -1 || dateIndex === undefined) {
                    return null;
                }
                return `${blockId}.${String(sentiment).toLowerCase()}.${dateIndex}`;
            }
            default:
                return null;
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
